import { parse } from 'tldts';
export class DomainUtils {
    /**
     * Checks if two given domain names or URLs belong to the same domain.
     * This method uses the `tldts` library to parse the domain names from the given inputs,
     * allowing it to accurately handle a wide range of domain and subdomain structures,
     * as well as different schemes (protocols).
     *
     * @param root The root domain name or URL to compare. This can be a full URL or just a domain name.
     * @param target The target domain name or URL to compare against the root. Similar to the root,
     *               it can be a full URL or just a domain name.
     * @returns `true` if both the root and target belong to the same domain, `false` otherwise.
     *
     * @example
     * // Returns true
     * DomainUtils.isSameDomain('https://example.com', 'https://sub.example.com')
     *
     * @example
     * // Returns false
     * DomainUtils.isSameDomain('https://example.com', 'https://anotherdomain.com')
     */
    static isSameDomain(root, target) {
        const rootDomain = this.getDomain(root);
        const targetDomain = this.getDomain(target);
        return rootDomain === targetDomain;
    }
    /**
     * Extracts the domain name from a given hostname or URL.
     * This method uses the `tldts` library to parse the given input, allowing it to accurately handle
     * a wide range of domain and subdomain structures, as well as different schemes (protocols).
     * It is useful for extracting the domain part of a URL or a hostname to perform operations
     * that require just the domain name.
     *
     * @param hostname The hostname or URL from which to extract the domain name. This can be a full URL,
     *                 including protocol and path, or just a domain name.
     * @returns The domain name extracted from the given hostname or URL.
     *
     * @throws Will throw an error if the domain cannot be extracted from the given hostname,
     * indicating either an invalid input or a parsing issue.
     *
     * @example
     * // Returns "example.com"
     * DomainUtils.getDomain('https://sub.example.com/path?query=string')
     *
     * @example
     * // Returns "example.com"
     * DomainUtils.getDomain('example.com')
     *
     * @example
     * // Throws an error for invalid hostnames
     * DomainUtils.getDomain('not_a_valid_hostname')
     */
    static getDomain(hostname) {
        let host = hostname;
        try {
            host = new URL(hostname).hostname;
        }
        catch {
            // Input wasn't a parseable URL — treat it as a hostname directly.
        }
        const result = parse(host);
        return result.domain ?? host;
    }
    static isSameHostname(root, target) {
        try {
            return new URL(root).hostname === new URL(target).hostname;
        }
        catch {
            return false;
        }
    }
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoiZG9tYWluLmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vc3JjL2RvbWFpbi50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQSxPQUFPLEVBQUUsS0FBSyxFQUFFLE1BQU0sT0FBTyxDQUFDO0FBRTlCLE1BQU0sT0FBTyxXQUFXO0lBQ3RCOzs7Ozs7Ozs7Ozs7Ozs7Ozs7T0FrQkc7SUFDSSxNQUFNLENBQUMsWUFBWSxDQUFDLElBQVksRUFBRSxNQUFjO1FBQ3JELE1BQU0sVUFBVSxHQUFHLElBQUksQ0FBQyxTQUFTLENBQUMsSUFBSSxDQUFDLENBQUM7UUFDeEMsTUFBTSxZQUFZLEdBQUcsSUFBSSxDQUFDLFNBQVMsQ0FBQyxNQUFNLENBQUMsQ0FBQztRQUM1QyxPQUFPLFVBQVUsS0FBSyxZQUFZLENBQUM7SUFDckMsQ0FBQztJQUVEOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7O09BeUJHO0lBQ0ksTUFBTSxDQUFDLFNBQVMsQ0FBQyxRQUFnQjtRQUN0QyxJQUFJLElBQUksR0FBRyxRQUFRLENBQUM7UUFDcEIsSUFBSTtZQUNGLElBQUksR0FBRyxJQUFJLEdBQUcsQ0FBQyxRQUFRLENBQUMsQ0FBQyxRQUFRLENBQUM7U0FDbkM7UUFBQyxNQUFNO1lBQ04sa0VBQWtFO1NBQ25FO1FBQ0QsTUFBTSxNQUFNLEdBQUcsS0FBSyxDQUFDLElBQUksQ0FBQyxDQUFDO1FBQzNCLE9BQU8sTUFBTSxDQUFDLE1BQU0sSUFBSSxJQUFJLENBQUM7SUFDL0IsQ0FBQztJQUVNLE1BQU0sQ0FBQyxjQUFjLENBQUMsSUFBWSxFQUFFLE1BQWM7UUFDdkQsSUFBSTtZQUNGLE9BQU8sSUFBSSxHQUFHLENBQUMsSUFBSSxDQUFDLENBQUMsUUFBUSxLQUFLLElBQUksR0FBRyxDQUFDLE1BQU0sQ0FBQyxDQUFDLFFBQVEsQ0FBQztTQUM1RDtRQUFDLE1BQU07WUFDTixPQUFPLEtBQUssQ0FBQztTQUNkO0lBQ0gsQ0FBQztDQUNGIn0=